'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Workflows } from '@/components/workflows';
import { Urls } from '@/lib/urls';
import { PencilIcon } from '@heroicons/react/16/solid';
import { useRouter } from 'next/navigation';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect, useMemo } from 'react';
import {
  useWorkflows,
  type WorkflowSettings,
} from '@/providers/workflow-provider';
import { AnimatedIndicator } from '@/components/ui/animated-indicator';
import { BookmarkPlusIcon } from 'lucide-react';

function EmptyWorkflows() {
  return (
    <div className='flex h-full flex-col items-center justify-center gap-0.5 pb-5 text-center'>
      <span className='text-sm font-medium'>Inga arbetsflöden</span>
      <span className='text-xs text-muted-foreground'>
        Listor och tabeller kan sparas som arbetsflöden genom att klicka på{' '}
        <BookmarkPlusIcon className='inline-block size-4' /> uppe till höger på
        sidan du vill spara.
      </span>
    </div>
  );
}

export function WorkflowsWidget() {
  const router = useRouter();
  const {
    sharedFolders,
    userWorkflows,
    workEngagementWorkflows,
    isLoadingUserWorkflows,
    isLoadingWorkEngagementWorkflows,
    workflowSettings,
    setWorkflowSettings,
  } = useWorkflows();

  useEffect(() => {
    if (isLoadingUserWorkflows || isLoadingWorkEngagementWorkflows) return;
    if (!userWorkflows || !workEngagementWorkflows) return;

    const dashboardIds = workflowSettings.expandedItems.dashboard;

    // Om dashboard-key inte finns ännu
    if (!dashboardIds) {
      const allFolders = [
        ...(userWorkflows?.folders ?? []),
        ...(workEngagementWorkflows?.flatMap((wf) => wf.folders ?? []) ?? []),
      ];

      if (allFolders.length > 0) {
        const pinnedDashboardIds = allFolders.map((folder) => folder.client_id);

        const newSettings: WorkflowSettings = {
          ...workflowSettings,
          expandedItems: {
            ...workflowSettings.expandedItems,
            dashboard: pinnedDashboardIds,
          },
        };

        setWorkflowSettings(newSettings);
      }
    }
  }, [
    isLoadingUserWorkflows,
    isLoadingWorkEngagementWorkflows,
    userWorkflows,
    workEngagementWorkflows,
    workflowSettings,
    setWorkflowSettings,
  ]);

  const isFetching = isLoadingUserWorkflows || isLoadingWorkEngagementWorkflows;
  const hasSharedFolders = sharedFolders && sharedFolders.length > 0;

  const isEmptyWorkflows = useMemo(() => {
    const checkUser = userWorkflows?.folders?.some((folder) => folder.pinned);
    const checkWorkEngagement = workEngagementWorkflows?.some(
      (workEngagement) =>
        workEngagement.folders?.some((folder) => folder.pinned)
    );
    return !checkUser && !checkWorkEngagement;
  }, [userWorkflows, workEngagementWorkflows]);

  return (
    <Card className='h-full gap-0 p-0'>
      <CardHeader className='flex flex-row items-center justify-between gap-2 border-b-0 px-4 pt-4 text-sm font-medium'>
        <CardTitle className='flex items-center gap-2'>Arbetsflöden</CardTitle>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className='relative'
              variant={'outline'}
              size={'icon'}
              onClick={() => router.push(Urls.dashboard.workflows)}
            >
              {hasSharedFolders && <AnimatedIndicator />}
              <PencilIcon className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='font-normal'>Redigera</TooltipContent>
        </Tooltip>
      </CardHeader>
      <CardContent className='relative px-4 pb-4'>
        {isFetching ? (
          <div className='flex flex-col gap-4'>
            {Array.from({ length: 2 }, (_, i) => (
              <Skeleton key={i} className='h-12 w-full' />
            ))}
          </div>
        ) : isEmptyWorkflows ? (
          <EmptyWorkflows />
        ) : (
          <>
            <Workflows isDashboard />
            {workEngagementWorkflows?.map((workEngagementWorkflow) => (
              <Workflows
                key={workEngagementWorkflow.work_engagement_id}
                workEngagementId={workEngagementWorkflow.work_engagement_id}
                isDashboard
              />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
